import { UserProfile } from 'fitbeat.gym-core';
import {
  CouponAppliesTo,
  IPaymentSetupData,
  ISubscriptionPaymentSummary,
  StatusMessageType,
} from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import { AppState, AppStateStatus, Platform, StyleSheet, View } from 'react-native';
import { connect } from 'react-redux';
import { hasAccessToAStudioGymSelector } from '../auth/auth-selectors';
import { IAppStore } from '../HomeWorkouts';
import { UserProfileService } from '../services';
import { GoogleAnalytics } from '../services/google-analytics';
import Theme from '../theme';
import { LoadingModal } from '../ui';
import { clearDeepLinkParams, defaultScale, Logger, UserDataConsumer } from '../utils';
import { createUnauthenticatedUserServiceClient, createUserServiceClient } from '../utils/apiClientFactory';
import { InvalidAccountChangeMessage } from './components/InvalidAccountChangeMessage';
// @ts-ignore
import { PaymentSummaryScreenContent } from './components/PaymentSummaryScreenContent';
import { SubscriptionScreenStatusMessage } from './components/SubscriptionScreenStatusMessage';
import { closePaywallModalAction, setSubscriptionSummaryAndPaymentSetupData } from './redux/payments-actions';
import { openMembershipPlans } from './utils';

interface IState {
  isLoading: boolean;
  appState: AppStateStatus;
  error: string | null;
  submitError: string | null;
  total: null | number;
}

interface IProps {
  user?: {
    userProfile: UserProfile;
    userProfileService: UserProfileService;
  };
  clearDeepLinkParams: (params: string[]) => void;
  setSummaryAndPaymentData: (
    subscriptionSummary: ISubscriptionPaymentSummary, paymentSetupData: IPaymentSetupData | null) => void;
  navigation: any;
  isUserAuthenticated: boolean;
  hasAccessToAStudioGym: boolean;
  userId?: string;
  modalMode?: boolean;
  planId?: string;
  gymName?: string;
  coupon?: string;
  onceoff?: string;
  statusMessage?: string;
  statusMessageType?: StatusMessageType;
  pendingPlanId?: string;
  pendingCoupon?: string;
  stripeCustomerId?: string;
  billingStartDate?: string;
  specialOfferId?: string;
  paymentSubscriptionSummary: ISubscriptionPaymentSummary | null;
  paymentSetupData: IPaymentSetupData | null;
  onJoinSuccess: () => void;
  reloadAvailableGyms: () => void;
  shouldShowMembershipPlansButton: boolean;
  closePaywallModal: () => void;
  t: (key: string, options?: any) => string;
}

class PreLoadedPlanDetails extends React.Component<IProps, IState> {
  private readonly unauthenticatedUserServiceClient = createUnauthenticatedUserServiceClient();
  private readonly userServiceClient = createUserServiceClient();
  private readonly googleAnalytics = new GoogleAnalytics();

  constructor(props) {
    super(props);

    const total = props.paymentSubscriptionSummary &&
      props.paymentSubscriptionSummary.totalIncludingOnceOffPrices.toFixed(2);

    this.state = {
      total,
      isLoading: false,
      appState: AppState.currentState,
      error: null,
      submitError: null,
    };
  }

  public async componentDidMount() {
    const { planId, modalMode, hasAccessToAStudioGym} = this.props;

    if (!modalMode) {
      // Used to determine which image/sliders to use on web
      this.props.navigation.setParams({ hasAccessToAStudioGym });
    }

    const shouldRedirectToPlanSelection = hasAccessToAStudioGym && !planId;
    if (shouldRedirectToPlanSelection) {
      return this.props.navigation.navigate('ExternalPlanSelection');
    }

    await this.loadPaymentInfo();
  }

  public async componentDidUpdate(newProps) {
    const {
      planId,
      coupon,
      user,
    } = this.props;
    if (
      (planId && newProps.planId !== planId) ||
      (coupon && newProps.coupon !== coupon) ||
      (user?.userProfile.stripeUserId !== newProps.user?.userProfile.stripeUserId)) {
      await this.loadPaymentInfo();
    }
  }

  private loadPaymentInfo = async () => {
    const {
      userId, gymName, coupon, onceoff, pendingPlanId, pendingCoupon, planId, isUserAuthenticated, specialOfferId,
    } = this.props;
    try {
      if (!userId) {
        return this.setState({
          error: 'A userId is required to complete a subscription. Please contact support' });
      }

      this.setState({ isLoading: true, error: null });

      if (planId && isUserAuthenticated) {
        const accountChangeValidation = await this.userServiceClient.getStripeAccountValidation(
          planId, gymName);
        if (accountChangeValidation.accountChangeNeeded) {
          return this.props.navigation.navigate('StripeAccountChangeConfirmation', { accountChangeValidation });
        }
      }

      const paymentSubscriptionSummary = await this.getSubscriptionPaymentSummary(userId);

      let paymentSetupData = null;
      if (paymentSubscriptionSummary.paymentMethodRequired || this.state.submitError) {
        paymentSetupData = await this.unauthenticatedUserServiceClient.createSetupIntentForCustomer(
          userId,
          coupon ? coupon : '',
          onceoff ? onceoff : '',
          paymentSubscriptionSummary.planId,
          pendingPlanId,
          pendingCoupon,
          specialOfferId,
        );
      }

      this.props.setSummaryAndPaymentData(paymentSubscriptionSummary, paymentSetupData);
    } catch (e) {
      const error = e as Error;
      this.setState({ error: error.message || 'Failed to load payment summary.' });
      this.logErrorToBugsnag(error, 'Failed to get payment summary');
    } finally {
      this.setState({ isLoading: false });
    }
  }

  private logErrorToBugsnag(error: Error, failureDescription: string) {
    const {
      userId, coupon, pendingPlanId, pendingCoupon, planId, specialOfferId,
    } = this.props;
    Logger.notify(error, (report) => {
      report.addMetaData('description', failureDescription);
      report.addMetaData('uid', userId);
      report.addMetaData('coupon', coupon);
      report.addMetaData('planId', planId);
      report.addMetaData('pendingPlanId', pendingPlanId);
      report.addMetaData('pendingCoupon', pendingCoupon);
      report.addMetaData('specialOfferId', specialOfferId);
    });
  }

  private clearAccountRelatedDeepLinkParams = () => {
    this.props.clearDeepLinkParams([
      'first_name',
      'last_name',
      'email',
      'stripe_customer_id',
      'onboarding',
      'pendingPlanId',
      'pendingCoupon',
      'route',
      'planId',
      'coupon',
      'billingStartDate',
      'specialOfferId',
    ]);
  }

  private getSubscriptionPaymentSummary = async (userId: string) => {
    const {
      coupon,
      onceoff,
      pendingPlanId,
      pendingCoupon,
      stripeCustomerId,
      planId,
    } = this.props;
    return await this.unauthenticatedUserServiceClient.getSubscriptionPaymentSummary(
      userId,
      planId,
      coupon,
      onceoff,
      pendingPlanId,
      pendingCoupon,
      stripeCustomerId,
    );
  }

  private seeMembershipPlans = async () => {
    // Todo: Make the membership plans endpoint public
    const { user } = this.props;
    await openMembershipPlans(user?.userProfile);
  }

  private onSubmit = async () => {
    this.googleAnalytics.logEventWithParamaters('sign_up_capture_payment_details_start');
    const {
      user,
      userId,
      navigation,
      paymentSubscriptionSummary,
      pendingPlanId,
      pendingCoupon,
      stripeCustomerId,
      modalMode,
      isUserAuthenticated,
      specialOfferId,
      onJoinSuccess,
      t,
    } = this.props;
    try {
      if (!userId) {
        return this.setState({
          error: 'A userId is required to complete a subscription. Please contact support.' });
      }

      this.setState({ isLoading: true, submitError: null });

      if (paymentSubscriptionSummary) {
        const couponId = paymentSubscriptionSummary.coupon ?
          paymentSubscriptionSummary.coupon.id : undefined;
        const subscription = await this.unauthenticatedUserServiceClient.createSubscription(
          userId,
          paymentSubscriptionSummary.planId,
          couponId,
          pendingPlanId,
          pendingCoupon,
          stripeCustomerId,
          specialOfferId,
        );

        if (subscription.status === 'incomplete') {
          this.logErrorToBugsnag(new Error('Created an incomplete subscription'), 'Subscription incomplete');
        }
        if (modalMode && onJoinSuccess) {
          // Hack to prevent navigation to payments screen from summary dashboard
          this.props.clearDeepLinkParams(['route']);
          await onJoinSuccess();
        } else {
          if (isUserAuthenticated && user) {
            await user.userProfileService.refreshCurrentUserProfile();
            navigation.navigate('HomeLoading');
          } else {
            if (Platform.OS === 'web') {
              navigation.navigate('HomeInstallAppScreen');
            } else {
              navigation.navigate('Welcome');
            }
          }
        }
      }
      this.clearAccountRelatedDeepLinkParams();
    } catch (error) {
      this.setState({
        submitError: (error as Error).message || t('subscriptionCreateErrorMessage'),
        isLoading: false,
      });
      this.logErrorToBugsnag(error as Error, 'Failed to create subscription');
      this.loadPaymentInfo().catch(
        (error) => this.logErrorToBugsnag(error, 'Failed to create subscription'),
      );
    }
  }

  private goBack = () => {
    const { navigation, isUserAuthenticated, modalMode, closePaywallModal } = this.props;
    if (modalMode) {
      closePaywallModal();
    } else {
      if (isUserAuthenticated) {
        navigation.navigate('HomeLoading');
      } else {
        navigation.navigate('AuthLoading');
      }
    }
    this.clearAccountRelatedDeepLinkParams();
  }

  // tslint:disable-next-line:member-ordering
  public render() {
    const {
      isLoading,
      error,
      submitError,
    } = this.state;

    if (isLoading) {
      return (
        <View style={styles.loadingContainer}>
          <LoadingModal isLoading={true} />
        </View>
      );
    }

    const {
      statusMessage,
      statusMessageType,
      modalMode,
      paymentSubscriptionSummary,
      paymentSetupData,
      navigation,
      stripeCustomerId,
      hasAccessToAStudioGym,
      specialOfferId,
      billingStartDate,
      userId,
      shouldShowMembershipPlansButton,
      user,
      t,
    } = this.props;

    const  { allowAddRemoveCoupon, shouldGetCardDetails, shouldShowContinueButton } = this.getButtonDisplayData();

    const total = paymentSubscriptionSummary?.totalIncludingOnceOffPrices.toFixed(2);

    const accountChangePossible = paymentSubscriptionSummary?.accountStatusChangeValidation?.success;

    const impossibleToProceedError = error || statusMessage;
    if (impossibleToProceedError) {
      return <SubscriptionScreenStatusMessage
        message={impossibleToProceedError}
        messageType={statusMessageType}
        goBack={this.goBack}
        shouldShowSeeMembershipPlansButton={shouldShowMembershipPlansButton}
        seeMembershipPlans={this.seeMembershipPlans}
        t={t}
      />;
    }

    return (
      <View style={[styles.container, modalMode && styles.modalModeContainer]}>
        {!accountChangePossible &&
          <InvalidAccountChangeMessage
            accountStatusChangeValidation={paymentSubscriptionSummary?.accountStatusChangeValidation}
            goBack={this.goBack}
            t={t}
          />
        }
        {accountChangePossible &&
          <PaymentSummaryScreenContent
            paymentSubscriptionSummary={paymentSubscriptionSummary}
            t={t}
            total={total}
            allowAddRemoveCoupon={allowAddRemoveCoupon}
            stripeCustomerId={stripeCustomerId}
            paymentMethodAdded={this.onSubmit}
            paymentSetupData={paymentSetupData}
            seeMembershipPlans={this.seeMembershipPlans}
            shouldGetCardDetails={shouldGetCardDetails}
            shouldShowContinueButton={shouldShowContinueButton}
            navigation={navigation}
            modalMode={modalMode}
            hasAccessToAStudioGym={hasAccessToAStudioGym}
            billingStartDate={billingStartDate}
            userId={userId}
            shouldShowMembershipPlansButton={shouldShowMembershipPlansButton}
            userProfile={user?.userProfile}
            submitError={submitError}
            goBack={this.goBack}
          />
        }
      </View>
    );
  }

  private getButtonDisplayData = (): {
    allowAddRemoveCoupon: boolean,
    shouldGetCardDetails: boolean,
    shouldShowContinueButton: boolean,
  } => {
    const allowAddRemoveCoupon = this.showCouponWidget();
    const shouldGetCardDetails = this.shouldRequestCardDetails();
    const shouldShowContinueButton = this.userHasPaymentMethodShowContinue(shouldGetCardDetails);

    return { allowAddRemoveCoupon, shouldGetCardDetails, shouldShowContinueButton };
  }

  private showCouponWidget = (): boolean => {
    const { paymentSubscriptionSummary, specialOfferId } = this.props;

    if (specialOfferId) {
      return false;
    }
    const couponAppliesToCustomer =
      paymentSubscriptionSummary?.coupon?.metadata.couponAppliesTo === CouponAppliesTo.customer;
    if (couponAppliesToCustomer) {
      return false;
    }
    return true;
  }

  private shouldRequestCardDetails = (): boolean => {
    const { submitError } = this.state;
    const { paymentSubscriptionSummary, paymentSetupData } = this.props;

    if (paymentSubscriptionSummary && paymentSetupData) {
      const previousAttemptToSubscribeFailed = !!submitError;
      const paymentMethodRequired = paymentSubscriptionSummary.paymentMethodRequired;
      return paymentMethodRequired || previousAttemptToSubscribeFailed;
    }
    return false;
  }

  private userHasPaymentMethodShowContinue = (shouldGetCardDetails: boolean): boolean => {
    const { paymentSubscriptionSummary } = this.props;
    return !!(paymentSubscriptionSummary && !shouldGetCardDetails);
  }
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.WHITE,
    height: '100%',
  },
  modalModeContainer: {
    ...Platform.select({
      web: {
        paddingTop: defaultScale(40),
      },
    }),
  },
  loadingContainer: {
    flex: 1,
    padding: 20,
    height: '100%',
    justifyContent: 'center',
    backgroundColor: Theme.WHITE,
  },
  membershipButton: {
    marginBottom: 20,
  },
});

const mapStateToProps = (state: IAppStore, ownProps: Partial<IProps>): Partial<IProps> => {
  const authenticatedUser = ownProps.user?.userProfile;
  const hasAccessToAStudioGym = hasAccessToAStudioGymSelector(state);
  return {
    planId: state.deepLinkParamaters.planId,
    coupon: state.deepLinkParamaters.coupon,
    onceoff: state.deepLinkParamaters.onceoff,
    statusMessage: state.deepLinkParamaters.statusMessage,
    statusMessageType: state.deepLinkParamaters.statusMessageType,
    pendingPlanId: state.deepLinkParamaters.pendingPlanId,
    pendingCoupon: state.deepLinkParamaters.pendingCoupon,
    gymName: state.deepLinkParamaters.gymName,
    stripeCustomerId: authenticatedUser?.stripeUserId || state.deepLinkParamaters.stripe_customer_id,
    paymentSubscriptionSummary: state.payments.paymentSubscriptionSummary,
    paymentSetupData: state.payments.paymentSetupData,
    userId: authenticatedUser?.uid || state.deepLinkParamaters.userId,
    hasAccessToAStudioGym,
    isUserAuthenticated: !!authenticatedUser,
    billingStartDate: state.deepLinkParamaters.billingStartDate,
    specialOfferId: state.deepLinkParamaters.specialOfferId,
    shouldShowMembershipPlansButton:
      hasAccessToAStudioGym && !state.deepLinkParamaters.specialOfferId,
  };
};

const mapDispatchToProps = (dispatch, ownProps): Partial<IProps> => {
  return {
    setSummaryAndPaymentData: (paymentSubscriptionSummary, paymentSetupData) =>
      dispatch(setSubscriptionSummaryAndPaymentSetupData(paymentSubscriptionSummary, paymentSetupData)),
    clearDeepLinkParams: (keysToClear) => dispatch(clearDeepLinkParams(keysToClear)),
    closePaywallModal: () => dispatch(closePaywallModalAction()),
  };
};

export default UserDataConsumer(connect(mapStateToProps, mapDispatchToProps)(translate(
  ['payments'], { wait: true })(PreLoadedPlanDetails)));
